import React, { useState, useEffect, useRef } from 'react';
import { useStoreAuth } from '@/context/StoreAuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogIn, Clock, AlertCircle } from 'lucide-react';
import loginBackground from '@/assets/login-background.png';
import TurnstileCaptcha, { type TurnstileCaptchaHandle } from '@/components/auth/TurnstileCaptcha';
import LoginBannerSlider from '@/components/auth/LoginBannerSlider';
import LoginBannerMiniCard from '@/components/auth/LoginBannerMiniCard';
import { isValidEmail } from '@/utils/emailValidation';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import useBrandSettings from '@/hooks/useBrandSettings';
import { clearEnvironmentChoice } from '@/lib/environmentChoice';
import { 
  getRememberMePreference, 
  prepareAuthStorage 
} from '@/utils/authStorage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  COMPANY_ADDRESS,
  COMPANY_CNPJ,
  COMPANY_EMAIL,
  COMPANY_NAME,
  PRIVACY_POLICY_PATH,
} from '@/constants/company';

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const [rememberMe, setRememberMe] = useState(() => getRememberMePreference());
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const { login, user } = useStoreAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: brandSettings } = useBrandSettings();
  const captchaRef = useRef<TurnstileCaptchaHandle>(null);
  const redirectingRef = useRef(false);

  const clearLoginError = () => {
    if (loginError) setLoginError(null);
  };

  // Buscar configurações do sistema
  const { data: systemSettings } = useQuery({
    queryKey: ['system-settings-public'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_system_settings');
      if (error) {
        console.warn('Erro ao buscar configurações:', error);
        return null;
      }
      const settings: Record<string, any> = {};
      (data || []).forEach((setting: any) => {
        settings[setting.key] = setting.value;
      });
      return settings;
    },
    staleTime: 60000,
  });

  const turnstileEnabled = systemSettings?.turnstile_enabled ?? true;
  const turnstileSiteKey = systemSettings?.turnstile_site_key ?? '0x4AAAAAAB-eeAgUtKF8S5MQ';

  // Se já autenticado (ex.: abriu /login em nova aba), redirecionar imediatamente.
  // Fonte única de verdade: user do StoreAuthContext (evita corrida com getSession()).
  useEffect(() => {
    if (!user || isLoading || redirectingRef.current) return;
    const target = user.isPlatformAdmin
      ? '/super-admin'
      : '/escolher-ambiente';
    navigate(target, { replace: true });
  }, [user, navigate, isLoading]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpar mensagem de erro anterior ao tentar novamente
    clearLoginError();

    // Normalizar credenciais (saneamento de bordas e casing do email)
    const sanitizedEmail = credentials.email.trim().toLowerCase();
    const sanitizedPassword = credentials.password.trim();

    // Validação de email antes de submeter
    if (!isValidEmail(sanitizedEmail)) {
      toast({
        title: 'Email inválido',
        description: 'Por favor, insira um email válido.',
        variant: 'destructive',
      });
      return;
    }

    if (!sanitizedPassword) {
      toast({
        title: 'Senha obrigatória',
        description: 'Por favor, insira sua senha.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    // Preparar storage ANTES do login baseado na preferência
    prepareAuthStorage(rememberMe);
    clearEnvironmentChoice();

    try {
      console.log('🔑 Login.tsx - Chamando login...');
      redirectingRef.current = true;
      await login(sanitizedEmail, sanitizedPassword, turnstileEnabled ? captchaToken : undefined);
      console.log('🔑 Login.tsx - login() completado, aplicando hard-navigation');

      // Hard-navigation: descarta totalmente a árvore do Login para evitar
      // qualquer sobreposição residual com o layout do StoreIndex.
      // Descobrimos qual é o destino consultando a sessão diretamente,
      // já que o user do contexto pode não ter propagado ainda.
      const { data: { user: authUser } } = await supabase.auth.getUser();
      let target = '/escolher-ambiente';
      if (authUser?.id) {
        try {
          const [{ data: isAdmin }, { data: roles }] = await Promise.all([
            supabase.rpc('is_platform_admin', { _user_id: authUser.id }),
            supabase.from('user_network_roles').select('role').eq('user_id', authUser.id).eq('is_active', true),
          ]);
          if (isAdmin) target = '/super-admin';
          else if ((roles?.length ?? 0) > 0 && roles?.every((item) => item.role === 'BROKER_PARTNER')) target = '/portal-corretor';
          else if (!roles?.some((item) => item.role === 'BROKER_PARTNER')) target = '/inicio';
        } catch (e) {
          console.warn('Login.tsx - falha ao checar is_platform_admin, indo para /inicio', e);
        }
      }
      // iOS standalone: trocar de tela com o teclado ainda fechando deixa o viewport encolhido.
      // Fecha o teclado e espera a animação terminar de verdade: altura visível parada por
      // ~200 ms, igual à altura da janela (< 2px) e sem deslocamento (máx 1500 ms).
      try {
        (document.activeElement as HTMLElement | null)?.blur?.();
        await new Promise<void>((resolve) => {
          const vv = window.visualViewport;
          if (!vv) { resolve(); return; }
          const start = performance.now();
          let lastHeight = vv.height;
          let lastChange = start;
          const tick = () => {
            const now = performance.now();
            if (Math.abs(vv.height - lastHeight) > 0.5) {
              lastHeight = vv.height;
              lastChange = now;
            }
            const settled = now - lastChange >= 200
              && Math.abs(window.innerHeight - vv.height) < 2
              && Math.round(vv.offsetTop) === 0;
            if (settled || now - start > 1500) { resolve(); return; }
            requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
        window.scrollTo(0, 0);
      } catch { /* segue para a navegação */ }
      navigate(target, { replace: true });
      return;
    } catch (error: any) {
      redirectingRef.current = false;
      // Tratar erro de conta pendente
      if (error?.message === 'PENDING_ACCESS') {
        setShowPendingModal(true);
        return;
      }
      setLoginError('Email ou senha incorretos.');
      console.error('Login error:', error);

    } finally {
      setIsLoading(false);
      // Sempre resetar o captcha após o submit (token Turnstile é de uso único)
      setCaptchaToken(undefined);
      captchaRef.current?.reset();
    }
  };

  // No iOS, o "repique" da rolagem mostra o fundo do html/body (claro); deixa preto só no login.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.backgroundColor, body.style.backgroundColor];
    html.style.backgroundColor = '#000';
    body.style.backgroundColor = '#000';
    return () => {
      html.style.backgroundColor = prev[0];
      body.style.backgroundColor = prev[1];
    };
  }, []);

  return (
    <div 
      className="flex flex-col min-h-screen min-h-[100dvh] p-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      {/* Camada escura intensa */}
      <div className="absolute inset-0 bg-black/90"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.7)_100%)]"></div>

      <div className="relative z-10 flex flex-1 items-start justify-center py-6 lg:items-center">
        <div className="grid w-full max-w-7xl gap-6 lg:grid-cols-[35fr_65fr] animate-fade-in">
          <Card className="border border-white/10 bg-black/60 backdrop-blur-xl shadow-2xl rounded-3xl w-full max-w-md mx-auto lg:max-w-none">
          <CardHeader className="text-center pt-10 pb-6 sm:pt-12 sm:pb-6">
            <div className="flex flex-col items-center gap-3 mb-6">
              <div className="flex items-center justify-center gap-3">
                <img 
                  src={brandSettings?.symbolLight || brandSettings?.symbolDark || 'https://mdckcgxuuyspxxnxuiuc.supabase.co/storage/v1/object/public/brand-assets/brand/symbol_dark_1764955794247.png'} 
                  alt={brandSettings?.platformName || 'AutoFluxo'} 
                  className="h-14"
                />
                <img 
                  src="https://mdckcgxuuyspxxnxuiuc.supabase.co/storage/v1/object/public/brand-assets/brand/logo_text_dark_1764955810339.png"
                  alt={brandSettings?.platformName || 'AutoFluxo'} 
                  className="h-10"
                />
              </div>
              <p className="text-white/80 text-sm">
                {brandSettings?.platformTagline || 'Sistema de Gestão Automotiva'}
              </p>
            </div>
            <CardTitle className="text-white">Login</CardTitle>
            <CardDescription>
              Entre com suas credenciais para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-10 sm:pb-12">
            <form onSubmit={handleSubmit} className="space-y-4" method="post" action="#">
              <div className="space-y-2">
                <label htmlFor="login-email" className="sr-only">Email</label>
                <Input
                  id="login-email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  inputMode="email"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="Email"
                  value={credentials.email}
                  onChange={(e) => {
                    clearLoginError();
                    setCredentials(prev => ({ ...prev, email: e.target.value }));
                  }}
                  className="bg-black/30 border-white/20 text-white placeholder:text-white/70 backdrop-blur-sm focus:bg-black/40 focus:border-white/40"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="login-password" className="sr-only">Senha</label>
                <PasswordInput
                  id="login-password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="Senha"
                  value={credentials.password}
                  onChange={(e) => {
                    clearLoginError();
                    setCredentials(prev => ({ ...prev, password: e.target.value }));
                  }}
                  className="bg-black/30 border-white/20 text-white placeholder:text-white/70 backdrop-blur-sm focus:bg-black/40 focus:border-white/40"
                  required
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    className="border-white/40 data-[state=checked]:bg-imperial-red data-[state=checked]:border-imperial-red"
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm text-white/80 cursor-pointer select-none"
                  >
                    Permanecer logado
                  </label>
                </div>
                <Link 
                  to="/esqueci-senha" 
                  className="text-sm text-red-500/70 hover:text-red-500 transition-colors"
                >
                  Esqueci minha senha
                </Link>
              </div>
              <div className="space-y-2">
                <TurnstileCaptcha
                  ref={captchaRef}
                  enabled={turnstileEnabled}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(undefined)}
                  onError={() => setCaptchaToken(undefined)}
                />
              </div>
              {loginError && (
                <div 
                  role="alert"
                  className="rounded-md bg-red-500/15 border border-red-500/30 p-3 text-sm text-red-200 flex items-start gap-2"
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{loginError}</span>
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-imperial-red hover:bg-red-700"
                disabled={isLoading || (turnstileEnabled && !captchaToken)}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Entrando...
                  </span>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Entrar
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

          <div className="lg:hidden">
            <LoginBannerMiniCard />
          </div>

          <div className="hidden lg:block">
            <LoginBannerSlider />
          </div>
        </div>
      </div>

      {/* ===== RODAPÉ INSTITUCIONAL ===== */}
      <footer className="relative z-10 shrink-0 border-t border-white/10 py-1.5">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-start gap-x-8 gap-y-1 text-[10.5px] leading-[1.25] text-white/60 md:flex-row md:items-start md:justify-between md:text-[11px] md:leading-[1.3]">
          <div className="flex flex-col items-start gap-y-0.5">
            <p>© {new Date().getFullYear()} AutoFluxo. Todos os direitos reservados.</p>
            <p>{COMPANY_NAME}</p>
            <p>CNPJ {COMPANY_CNPJ}</p>
          </div>
          <div className="flex flex-col items-start gap-y-0.5 md:items-end md:text-right">
            <Link
              to={PRIVACY_POLICY_PATH}

              className="underline underline-offset-2 transition-colors hover:text-white/90"
            >
              Política de Privacidade
            </Link>
            <p>{COMPANY_ADDRESS}</p>
            <a
              href={`mailto:${COMPANY_EMAIL}`}
              className="transition-colors hover:text-white/90"
            >
              {COMPANY_EMAIL}
            </a>
          </div>
        </div>
      </footer>


      {/* Modal de Conta Pendente */}
      <AlertDialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="h-8 w-8 text-amber-600" />
              </div>
            </div>
            <AlertDialogTitle className="text-center">
              Conta Pendente de Aprovação
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-3">
              <span className="block">
                Sua conta foi criada com sucesso, mas ainda não possui
                acesso a nenhuma loja.
              </span>
              <span className="block text-sm text-muted-foreground">
                Aguarde um administrador ou proprietário de loja conceder
                acesso para você poder utilizar o sistema.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="justify-center sm:justify-center">
            <AlertDialogAction onClick={() => setShowPendingModal(false)}>
              Entendi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Login;
